import { Alert, Box, Link as ChakraLink, Flex, Spinner, Text, useToast } from "@chakra-ui/react";
import { useFormik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import * as Yup from "yup";

import { MESSAGE } from "@equidefi/constants/forms";
import { useBoolean, useDisclosure } from "@equidefi/hooks";
import { dayjs, isValidDateString } from "@equidefi/i18n";
import {
  useCancelCryptoPayment,
  useCreateCryptoPayment,
  usePaymentStatus,
  useResumePayment,
} from "@equidefi/portals/hooks/usePayment";
import { ConfirmModal, DateInput, Input, MaskedInput, Modal } from "@equidefi/ui";

import { BannerAlert } from "@equidefi/ui";

import { CancelButton } from "@/components/workflow/CancelButton";
import { WorkflowButton } from "@/components/workflow/WorkflowButton";
import { CRYPTO_COMPLETE_MESSAGE } from "@/pages/CryptoComplete";
import WorkflowForm from "@/pages/workflow/WorkflowForm";
import { CountrySelect } from "@/pages/workflow/address/CountrySelect";
import { ProcessingFeeSummary } from "./ProcessingFeeSummary";

const individualSchema = Yup.object().shape({
  first_name: Yup.string()
    .trim()
    .nullable()
    .min(2, "First name must be at least 2 characters")
    .required(MESSAGE.REQUIRED("First name")),
  last_name: Yup.string()
    .trim()
    .nullable()
    .min(2, "Last name must be at least 2 characters")
    .required(MESSAGE.REQUIRED("Last name")),
  birthdate: Yup.mixed()
    .required("Please include your birthdate, and you must be at least 18 years old")
    .test("valid-date", "Please enter a valid date", (value) => isValidDateString(value))
    .test("min-date", "Birth date cannot be this early", (value) => {
      return dayjs(value).isSameOrAfter(dayjs(new Date(1900, 0, 1)));
    })
    .test("max-date", "You must be at least 18 years old", (value) => {
      return dayjs(value).isSameOrBefore(dayjs().subtract(18, "years"));
    })
    .test("year-format", "Please enter a valid year", (value) => {
      return dayjs(value).year() <= 9999;
    }),
});

export const entityTypeSchema = Yup.object().shape({
  company: Yup.string()
    .nullable()
    .test("company-required-for-entity-or-trust", function (value) {
      const { account_type } = this.parent;
      if ((account_type === "ENTITY" || account_type === "TRUST") && !value?.trim()) {
        return this.createError({
          message: account_type === "TRUST" ? MESSAGE.REQUIRED("Trust Name") : MESSAGE.REQUIRED("Entity Name"),
        });
      }
      return true;
    }),
  ein: Yup.string()
    .nullable()
    .test("ein-required-for-entity-or-trust", function (value) {
      const { entity_address_country_code } = this.parent;
      if (!value?.trim()) {
        return this.createError({
          message: MESSAGE.REQUIRED(),
        });
      }

      if (entity_address_country_code !== "US" && value.trim().length > 20) {
        return this.createError({
          message: "Must not exceed 20 characters",
        });
      }
      if (entity_address_country_code === "US" && !/^\d{9}$/.test(value.trim())) {
        return this.createError({
          message: "Must be exactly 9 digits, numbers only (no dashes)",
        });
      }
      return true;
    }),
  entity_address_country_code: Yup.string()
    .min(2, "Country code must be 2 characters long")
    .max(2, "Country code must be 2 characters long")
    .nullable()
    .required("Please enter your country"),
});

export default function CryptoPayment({ investment, offering, analytics, redirectUrl }) {
  const [isLoading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [paymentRedirectUrl, setPaymentRedirectUrl] = useState(null);
  const [paymentId, setPaymentId] = useState(null);
  const navigate = useNavigate();
  const toast = useToast();
  const createPayment = useCreateCryptoPayment(investment?.id);
  const { data: status, refetch: refetchStatus } = usePaymentStatus(paymentId, {
    refetchOnMount: "always",
    // Poll while a payment is in flight regardless of whether the modal is open.
    // Detection can land after the investor closes the modal (slow/late BVNK
    // webhooks); if polling stopped on close they would be stranded on the
    // payment step instead of advancing (EPP-3307). The query is already gated
    // on `enabled: !!paymentId`, so this never polls before a payment exists.
    refetchInterval: (data) =>
      ["succeeded", "canceled", "expired", "refunded", "underpaid"].includes(data) ? false : 2_500,
  });
  const { data: existing, refetch: refetchPayment } = useResumePayment(investment?.id, {
    refetchOnMount: "always",
  });
  const cancelPayment = useCancelCryptoPayment(existing?.payment_id);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const response = await createPayment.mutateAsync({
        amount: investment.amount,
        ...data,
      });

      setIsOpen(true);
      setPaymentRedirectUrl(response.redirect_url);
      setPaymentId(response.payment_id);
      refetchStatus();
      refetchPayment();
    } catch (error) {
      console.error(error);
      toast({
        status: "error",
        description: error.response?.data?.errors?.join(". ") ?? "Something went wrong.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Navigate straight to the processing page; do NOT refetch the investment
  // here to prevent flashing screen.
  const navigateToNextStep = useCallback(() => {
    if (redirectUrl) {
      navigate(redirectUrl);
    } else if (investment.expedited) {
      navigate(`/offerings/${offering.slug}/${investment?.id}/agreement`);
    } else {
      navigate(`/offerings/${offering.slug}/${investment?.id}/address`);
    }
  }, [navigate, redirectUrl, investment?.expedited, offering?.slug, investment?.id]);

  useEffect(() => {
    if (paymentId && (status === "succeeded" || status === "detected" || status === "processing")) {
      setIsOpen(false);
      analytics.track("payment", {
        amount: investment.amount,
        type: "CRYPTO",
      });

      navigateToNextStep();
    }
  }, [paymentId, status]);

  useEffect(() => {
    const handler = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== CRYPTO_COMPLETE_MESSAGE) return;

      setIsOpen(false);
      navigateToNextStep();
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [navigateToNextStep]);

  const isExpedited = investment?.expedited || false;

  // A BVNK checkout is already open for this investment: submitting resumes it
  // instead of sending a new payload, so the form is locked and validity is moot.
  const hasExistingPayment = !!existing && Object.keys(existing).length > 0;

  // Expedited investments reuse the vault as-entered and cannot be edited here,
  // but they still submit a fresh payload, so they stay subject to validation.
  const isDisabled = isExpedited || hasExistingPayment;

  let Form = IndividualPayment;
  let schema = individualSchema;
  if (investment.vault.entity_type === "ENTITY") {
    Form = EntityPayment;
    schema = entityTypeSchema;
  }

  const { handleSubmit, setFieldTouched, setFieldValue, handleBlur, getFieldProps, errors, values, touched, isValid } =
    useFormik({
      initialValues: {
        first_name: investment.vault?.first_name,
        middle_name: investment.vault?.middle_name,
        last_name: investment.vault?.last_name,
        company: investment.vault?.company,
        birthdate:
          investment.vault?.birthdate && dayjs(investment.vault?.birthdate).isValid()
            ? dayjs(investment.vault?.birthdate).format("MM/DD/YYYY")
            : null,
        ein: investment.vault?.ein,
        entity_address_country_code: investment.vault?.entity_address_country_code,
      },
      enableReinitialize: true,
      validationSchema: schema,
      onSubmit,
      validateOnChange: true,
      validateOnBlur: true,
      validateOnMount: true,
    });

  return (
    <WorkflowForm onSubmit={handleSubmit}>
      <BannerAlert
        status="warning"
        title="Important Notice"
        titleLayout="inline"
        description={
          <>
            When you fund your investment with crypto, the funds are converted immediately to USDC. Should a refund take
            place, <strong>you will receive the refund in USDC for the investment amount.</strong>
          </>
        }
        mb={4}
      />
      <Form
        investment={investment}
        values={values}
        getFieldProps={getFieldProps}
        errors={errors}
        touched={touched}
        isValid={isValid}
        setFieldValue={setFieldValue}
        handleBlur={handleBlur}
        setFieldTouched={setFieldTouched}
        existing={existing}
        isDisabled={isDisabled}
        isExpedited={isExpedited}
      />
      <ProcessingFeeSummary investment={investment} offering={offering} paymentType="crypto" />
      <Flex>
        <WorkflowButton type="submit" isDisabled={!hasExistingPayment && (!isValid || isLoading)}>
          {hasExistingPayment ? "Resume Payment" : "Continue"}
        </WorkflowButton>

        {hasExistingPayment ? (
          <CancelButton
            ml={2}
            onClick={async () => {
              await cancelPayment.mutateAsync();
              await Promise.all([refetchPayment(), refetchStatus()]);
            }}>
            Cancel Payment
          </CancelButton>
        ) : null}
      </Flex>
      <CryptoPaymentModal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setLoading(false);
        }}
        investment={investment}
        redirectUrl={paymentRedirectUrl}
      />
    </WorkflowForm>
  );
}

const IndividualPayment = ({
  investment,
  touched,
  values,
  errors,
  getFieldProps,
  setFieldValue,
  setFieldTouched,
  isValid,
  existing,
  isExpedited = false,
  isDisabled,
}) => {
  return (
    <>
      <DisabledCryptoFormAlert
        profileUri={`/offerings/${investment.offering.slug}/${investment.id}/profile`}
        isExpedited={isExpedited}
        isDisabled={isDisabled}
        isExisting={!!existing}
        vault={investment?.vault}
        mb={5}
      />
      <Flex>
        <Input
          label="First Name"
          isRequired
          defaultValue={investment.vault.first_name}
          error={touched.first_name && errors.first_name}
          isInvalid={errors.first_name && touched.first_name}
          {...getFieldProps("first_name")}
          isDisabled={true} // Cannot change name on crypto payments
          mr={2}
        />
        <Input
          label="Last Name"
          isRequired
          defaultValue={investment.vault.last_name}
          error={touched.last_name && errors.last_name}
          isInvalid={errors.last_name && touched.last_name}
          isDisabled={true} // Cannot change name on crypto payments
          {...getFieldProps("last_name")}
        />
      </Flex>
      <Flex>
        <DateInput
          label="Date of Birth (MM/DD/YYYY)"
          name="birthdate"
          isDisabled={isDisabled}
          mt={0}
          value={values?.birthdate}
          placeholder="MM/DD/YYYY"
          onChange={(date) => {
            setFieldValue("birthdate", date);
          }}
          onBlur={() => {
            setFieldTouched("birthdate", true);
          }}
          error={errors.birthdate && touched.birthdate ? errors.birthdate : undefined}
          isInvalid={errors.birthdate && touched.birthdate}
          isRequired
        />
      </Flex>
    </>
  );
};

const DisabledCryptoFormAlert = ({
  isExpedited = false,
  isDisabled = false,
  isExisting = false,
  profileUri = null,
  vault,
  ...props
}) => {
  const message = useMemo(() => {
    if (isExisting) {
      return (
        <>
          <Text mb={3}>
            Your crypto transaction has already been started and the following information is locked. If you want to
            continue, please click resume payment.
          </Text>

          <Text>
            If you have submitted your payment on blockchain, please wait a few minutes for it to process. Do not submit
            another payment. Blockchain transactions can take a few minutes to be detected.
          </Text>
        </>
      );
    }

    if (isExpedited) {
      return "You are re-using your previously entered information for this transaction. If this information has changed, you must start a new investment with the new investment.";
    }

    if (vault?.entity_type === "INDIVIDUAL") {
      return (
        <>
          Wallet Owner name below is locked and cannot be changed as it must match the name of the investor. If you need
          to make an adjustment to your name, please return to the&nbsp;
          <ChakraLink as={Link} to={profileUri}>
            <u>profile page</u>
          </ChakraLink>{" "}
          to do so.
        </>
      );
    }

    return null;
  }, [profileUri, isExisting, isExpedited, vault]);

  if (!message) return null;

  return (
    <Alert
      status="info"
      borderRadius="lg"
      border="1px solid"
      borderColor="equidefi.blue"
      boxShadow="sm"
      flexDirection="column"
      alignItems="center"
      justifyContent="flex-start"
      {...props}>
      <Text textStyle="body2">{message}</Text>
    </Alert>
  );
};

const EntityPayment = ({
  investment,
  touched,
  values,
  errors,
  getFieldProps,
  setFieldValue,
  setFieldTouched,
  isValid,
  existing,
  isExpedited = false,
  isDisabled = false,
}) => {
  return (
    <>
      <DisabledCryptoFormAlert
        profileUri={`/offerings/${investment.offering.slug}/${investment.id}/profile`}
        isExpedited={isExpedited}
        isDisabled={isDisabled}
        isExisting={!!existing}
        vault={investment?.vault}
        mb={5}
      />
      <Flex>
        <Input
          label="Company/Entity Name"
          isRequired
          isDisabled={isDisabled}
          defaultValue={investment?.vault?.company}
          error={touched.company && errors.company}
          isInvalid={errors.company && touched.company}
          {...getFieldProps("company")}
          mr={2}
        />
        <CountrySelect
          fieldName="entity_address_country_code"
          setFieldValue={setFieldValue}
          selectedCountry={values.entity_address_country_code}
          isDisabled={isDisabled}
        />
      </Flex>
      <Flex>
        <MaskedInput
          label={investment.vault?.account_type === "ENTITY" ? "EIN" : "Tax ID Number (TIN)"}
          isRequired
          maxLength={values.entity_address_country_code === "US" ? 9 : 20}
          isDisabled={isDisabled}
          defaultValue={investment?.vault?.ein}
          error={touched.ein && errors.ein}
          isInvalid={touched.ein && errors.ein}
          fieldType={investment.vault?.account_type === "ENTITY" ? "EIN" : "TIN"}
          {...getFieldProps("ein")}
        />
      </Flex>
    </>
  );
};

const CryptoPaymentModal = ({ isOpen, onClose, redirectUrl }) => {
  const { isOpen: isConfirmOpen, onOpen: openConfirm, onClose: closeConfirm } = useDisclosure();
  const [isLoading, loading] = useBoolean(true);

  return (
    <>
      {/* Main payment modal */}
      <Modal
        isOpen={isOpen}
        onClose={openConfirm} // intercept close
        contentProps={{
          maxW: { base: "full", md: "none" },
          w: { base: "full", md: "auto" },
          minW: { base: "auto", md: "480px" },
        }}>
        <Box position="relative" mt={6}>
          <iframe
            src={redirectUrl}
            title="Crypto Payment"
            width={500}
            height={800}
            sandbox="allow-scripts allow-forms allow-popups-to-escape-sandbox allow-same-origin"
            onLoad={() => loading.off()}
            referrerPolicy="no-referrer"
          />
          {isLoading && (
            <Box
              position="absolute"
              top="calc(50% - 50px)"
              left="calc(50% - 30px)"
              width="100%"
              height="100%"
              zIndex={500}>
              <Spinner ml={5} />
              <Text>Loading …</Text>
            </Box>
          )}
        </Box>
      </Modal>

      <ConfirmModal
        isOpen={isConfirmOpen}
        title="Abandon Crypto Payment"
        confirmText="Dismiss"
        cancelText="Yes, abandon crypto payment"
        cancelButtonProps={{ colorScheme: "purple" }}
        contentProps={{ maxW: { base: "full", md: "540px" } }}
        onConfirm={closeConfirm}
        onCancel={() => {
          closeConfirm();
          loading.on();
          onClose();
        }}>
        <Text textStyle="body" mt={4} mb={8}>
          Are you sure you want to close this window? Your payment may still be processing. Please allow time for it to
          process on-chain, we will notify you when it has been detected.
        </Text>
      </ConfirmModal>
    </>
  );
};
